import { useMemo, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Plus, Trash2, Pencil, DownloadCloud, Radio, Ship, Unlink, Search, Cpu, CheckCircle2 } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { useFleetStore } from '@/stores/fleet';
import { Modal } from '@/features/admin/Modal';
import {
  chipsApi,
  STATUS_LABEL,
  type Chip,
  type ChipInput,
  type ChipStatus,
} from '@/api/endpoints/chips';
import type { Device } from '@/api/types';

/** Lê os dados de chip de um device (campos top-level ou em attributes). */
function deviceChip(d: Device): { icc: string; operadora: string; numero: string; fornecedor: string } {
  const a = d.attributes ?? {};
  const pick = (top: unknown, key: string): string => {
    if (typeof top === 'string' && top.trim()) return top.trim();
    const v = a[key];
    return typeof v === 'string' ? v : '';
  };
  return {
    icc: pick(d.icc, 'icc'),
    operadora: pick(d.operator, 'operator'),
    numero: pick(d.phone, 'phone'),
    fornecedor: pick(undefined, 'fornecedor'),
  };
}

const STATUS_CLS: Record<ChipStatus, string> = {
  estoque: 'bg-status-off/15 text-status-off',
  bancada_teste: 'bg-status-ready/15 text-status-ready',
  enviado_tecnico: 'bg-primary/10 text-primary',
  ativo: 'bg-status-running/10 text-status-running',
  suspenso: 'bg-status-blocked/10 text-status-blocked',
  cancelado: 'bg-muted text-muted-foreground',
};

const STATUSES: ChipStatus[] = [
  'estoque',
  'bancada_teste',
  'enviado_tecnico',
  'ativo',
  'suspenso',
  'cancelado',
];

const EMPTY: ChipInput = {
  icc: '',
  numero: '',
  operadora: '',
  fornecedor: '',
  plano_dados: '',
  status: 'estoque',
  device_uniqueid: null,
  device_name: null,
  custo_mensal: null,
  obs: '',
};

function fmtBRL(v: number | null | undefined): string {
  return (v ?? 0).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
}

export function ChipsPage() {
  const qc = useQueryClient();
  const [q, setQ] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [vincFilter, setVincFilter] = useState<string>('');
  const [editing, setEditing] = useState<Chip | null>(null);
  const [creating, setCreating] = useState(false);
  const [prefill, setPrefill] = useState<Partial<ChipInput> | null>(null);

  const devices = useFleetStore((s) => s.devices);
  const deviceList = useMemo(
    () => Array.from(devices.values()).sort((a, b) => a.name.localeCompare(b.name)),
    [devices],
  );

  const chipsQ = useQuery({
    queryKey: ['chips', statusFilter, vincFilter, q],
    queryFn: () =>
      chipsApi.list({
        status: statusFilter || undefined,
        q: q || undefined,
        vinculado: vincFilter === '' ? undefined : vincFilter === 'sim',
      }),
  });
  const statsQ = useQuery({ queryKey: ['chips', 'stats'], queryFn: () => chipsApi.stats() });
  // Lista completa (sem filtro) só para saber quais ICCID já estão cadastrados.
  const allChipsQ = useQuery({ queryKey: ['chips', 'all'], queryFn: () => chipsApi.list() });
  const registeredIccs = useMemo(
    () => new Set((allChipsQ.data ?? []).map((c) => c.icc)),
    [allChipsQ.data],
  );

  // Dispositivos que já têm dados de chip (ICCID) — candidatos a cadastro.
  const devicesWithChip = useMemo(
    () => deviceList.map((d) => ({ device: d, chip: deviceChip(d) })).filter((x) => x.chip.icc),
    [deviceList],
  );

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['chips'] });
  };

  const importMut = useMutation({
    mutationFn: () => chipsApi.importFromDevices(),
    onSuccess: (r) => {
      invalidate();
      alert(
        `Importação concluída:\n• ${r.imported} novos\n• ${r.updated} atualizados\n• ${r.skipped} sem ICCID (ignorados)\nde ${r.devices} devices.`,
      );
    },
    onError: (e) => alert(`Falha ao importar: ${(e as Error).message}`),
  });

  const deleteMut = useMutation({
    mutationFn: (id: number) => chipsApi.remove(id),
    onSuccess: invalidate,
  });

  const unlinkMut = useMutation({
    mutationFn: (id: number) => chipsApi.unlink(id),
    onSuccess: invalidate,
  });

  const chips = chipsQ.data ?? [];
  const stats = statsQ.data;

  return (
    <div className="mx-auto max-w-5xl space-y-4 p-4 sm:p-6">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div>
          <h1 className="flex items-center gap-2 text-xl font-bold">
            <Cpu className="h-5 w-5 text-primary" />
            Chips / SIM
          </h1>
          <p className="text-sm text-muted-foreground">
            Cadastro e ciclo de vida dos chips, vinculados às embarcações.
          </p>
        </div>
        <div className="flex gap-2">
          <Button
            variant="outline"
            onClick={() => importMut.mutate()}
            disabled={importMut.isPending}
            className="gap-1.5"
          >
            <DownloadCloud className="h-4 w-4" />
            {importMut.isPending ? 'Importando…' : 'Importar dos devices'}
          </Button>
          <Button onClick={() => setCreating(true)} className="gap-1.5">
            <Plus className="h-4 w-4" />
            Novo chip
          </Button>
        </div>
      </div>

      {/* Cards de status */}
      {stats && (
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-4 lg:grid-cols-8">
          <StatCard label="Total" value={String(stats.total)} />
          <StatCard label="Em estoque" value={String(stats.estoque)} />
          <StatCard label="Bancada/teste" value={String(stats.bancada_teste)} accent="text-status-ready" />
          <StatCard label="Enviado ao téc." value={String(stats.enviado_tecnico)} accent="text-primary" />
          <StatCard label="Ativos" value={String(stats.ativo)} accent="text-status-running" />
          <StatCard label="Com rastreador" value={String(stats.com_rastreador)} accent="text-status-running" />
          <StatCard label="Sem vínculo" value={String(stats.sem_vinculo)} accent="text-status-blocked" />
          <StatCard label="Custo/mês (ativos)" value={fmtBRL(stats.custo_mensal_ativos)} />
        </div>
      )}

      {/* Toolbar */}
      <div className="flex flex-wrap items-center gap-2">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
          <Input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Buscar ICCID, número ou embarcação…"
            className="pl-8"
          />
        </div>
        <select
          value={statusFilter}
          onChange={(e) => setStatusFilter(e.target.value)}
          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">Todos os status</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>
              {STATUS_LABEL[s]}
            </option>
          ))}
        </select>
        <select
          value={vincFilter}
          onChange={(e) => setVincFilter(e.target.value)}
          className="h-10 rounded-md border border-input bg-background px-3 text-sm"
        >
          <option value="">Vínculo: todos</option>
          <option value="sim">Com rastreador</option>
          <option value="nao">Sem rastreador</option>
        </select>
      </div>

      {/* Lista */}
      <Card>
        <CardContent className="p-0">
          {chipsQ.isLoading ? (
            <p className="p-6 text-center text-sm text-muted-foreground">Carregando…</p>
          ) : chips.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">
              Nenhum chip. Use “Importar dos devices” para trazer os chips já instalados.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {chips.map((c) => (
                <li key={c.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                  <div className="min-w-0 flex-1">
                    <div className="flex items-center gap-2">
                      <span className="truncate font-semibold">{c.icc}</span>
                      <span className={cn('rounded-full px-2 py-0.5 text-[10px] font-bold', STATUS_CLS[c.status])}>
                        {STATUS_LABEL[c.status]}
                      </span>
                    </div>
                    <p className="truncate text-xs text-muted-foreground">
                      {[c.operadora, c.numero, c.plano_dados].filter(Boolean).join(' · ') || '—'}
                    </p>
                  </div>
                  <div className="flex shrink-0 flex-col items-end gap-1 text-xs">
                    {/* Rastreador (IMEI) — vinculado ou não */}
                    {c.device_uniqueid ? (
                      <span
                        className="inline-flex items-center gap-1 rounded-full bg-status-running/10 px-2 py-0.5 font-medium text-status-running"
                        title={`Rastreador (IMEI): ${c.device_uniqueid}`}
                      >
                        <Radio className="h-3 w-3" />
                        <span className="max-w-[140px] truncate">{c.device_uniqueid}</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-muted-foreground">
                        <Radio className="h-3 w-3" />
                        sem rastreador
                      </span>
                    )}
                    {/* Embarcação — vinculada ou não */}
                    {c.device_name ? (
                      <span
                        className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2 py-0.5 font-medium text-primary"
                        title={`Embarcação: ${c.device_name}`}
                      >
                        <Ship className="h-3 w-3" />
                        <span className="max-w-[140px] truncate">{c.device_name}</span>
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 rounded-full bg-muted px-2 py-0.5 text-muted-foreground">
                        <Ship className="h-3 w-3" />
                        sem embarcação
                      </span>
                    )}
                    {c.custo_mensal != null && (
                      <p className="text-muted-foreground">{fmtBRL(c.custo_mensal)}/mês</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    {c.device_uniqueid && (
                      <button
                        type="button"
                        title="Desvincular"
                        onClick={() => {
                          if (window.confirm(`Desvincular o chip ${c.icc} da embarcação?`)) unlinkMut.mutate(c.id);
                        }}
                        className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
                      >
                        <Unlink className="h-4 w-4" />
                      </button>
                    )}
                    <button
                      type="button"
                      title="Editar"
                      onClick={() => setEditing(c)}
                      className="rounded-md p-1.5 text-muted-foreground hover:bg-accent"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    <button
                      type="button"
                      title="Excluir"
                      onClick={() => {
                        if (window.confirm(`Excluir o chip ${c.icc}? Essa ação não pode ser desfeita.`))
                          deleteMut.mutate(c.id);
                      }}
                      className="rounded-md p-1.5 text-status-blocked hover:bg-status-blocked/10"
                    >
                      <Trash2 className="h-4 w-4" />
                    </button>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>

      {/* Dispositivos que já têm dados de chip (cadastrados no Traccar) */}
      <Card>
        <CardContent className="p-0">
          <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border px-4 py-3">
            <div>
              <h2 className="flex items-center gap-2 text-sm font-bold">
                <Ship className="h-4 w-4 text-primary" />
                Dispositivos com dados de chip ({devicesWithChip.length})
              </h2>
              <p className="text-xs text-muted-foreground">
                Embarcações já cadastradas com ICCID/operadora/número. Cadastre o chip ou use “Importar dos devices”.
              </p>
            </div>
          </div>
          {devicesWithChip.length === 0 ? (
            <p className="p-6 text-center text-sm text-muted-foreground">
              Nenhum dispositivo com dados de chip preenchidos.
            </p>
          ) : (
            <ul className="divide-y divide-border">
              {devicesWithChip.map(({ device, chip }) => {
                const registered = registeredIccs.has(chip.icc);
                return (
                  <li key={device.id} className="flex flex-wrap items-center gap-3 px-4 py-3">
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold">{device.name}</p>
                      <p className="truncate text-xs text-muted-foreground">
                        {chip.icc}
                        {[chip.operadora, chip.numero].filter(Boolean).length
                          ? ` · ${[chip.operadora, chip.numero].filter(Boolean).join(' · ')}`
                          : ''}
                      </p>
                    </div>
                    {registered ? (
                      <span className="inline-flex items-center gap-1 rounded-full bg-status-running/10 px-2 py-0.5 text-[10px] font-bold text-status-running">
                        <CheckCircle2 className="h-3 w-3" /> Cadastrado
                      </span>
                    ) : (
                      <Button
                        size="sm"
                        variant="outline"
                        className="gap-1.5"
                        onClick={() =>
                          setPrefill({
                            icc: chip.icc,
                            operadora: chip.operadora || '',
                            numero: chip.numero || '',
                            fornecedor: chip.fornecedor || '',
                            device_uniqueid: device.uniqueId,
                            device_name: device.name,
                            status: 'ativo',
                          })
                        }
                      >
                        <Plus className="h-3.5 w-3.5" /> Cadastrar
                      </Button>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </CardContent>
      </Card>

      {(creating || editing || prefill) && (
        <ChipModal
          chip={editing}
          prefill={prefill ?? undefined}
          devices={deviceList}
          onClose={() => {
            setCreating(false);
            setEditing(null);
            setPrefill(null);
          }}
          onSaved={() => {
            invalidate();
            setCreating(false);
            setEditing(null);
            setPrefill(null);
          }}
        />
      )}
    </div>
  );
}

function StatCard({ label, value, accent }: { label: string; value: string; accent?: string }) {
  return (
    <Card>
      <CardContent className="p-3">
        <p className="text-[11px] font-medium text-muted-foreground">{label}</p>
        <p className={cn('text-lg font-bold', accent)}>{value}</p>
      </CardContent>
    </Card>
  );
}

function ChipModal({
  chip,
  prefill,
  devices,
  onClose,
  onSaved,
}: {
  chip: Chip | null;
  prefill?: Partial<ChipInput>;
  devices: Device[];
  onClose: () => void;
  onSaved: () => void;
}) {
  const isEdit = chip !== null;
  const [form, setForm] = useState<ChipInput>(
    chip
      ? {
          icc: chip.icc,
          numero: chip.numero ?? '',
          operadora: chip.operadora ?? '',
          fornecedor: chip.fornecedor ?? '',
          plano_dados: chip.plano_dados ?? '',
          status: chip.status,
          device_uniqueid: chip.device_uniqueid,
          device_name: chip.device_name,
          custo_mensal: chip.custo_mensal,
          obs: chip.obs ?? '',
        }
      : { ...EMPTY, ...(prefill ?? {}) },
  );
  const [err, setErr] = useState<string | null>(null);

  const set = <K extends keyof ChipInput>(k: K, v: ChipInput[K]) => setForm((f) => ({ ...f, [k]: v }));

  const onPickDevice = (uniqueId: string) => {
    if (!uniqueId) {
      set('device_uniqueid', null);
      set('device_name', null);
      return;
    }
    const d = devices.find((x) => x.uniqueId === uniqueId);
    setForm((f) => ({
      ...f,
      device_uniqueid: uniqueId,
      device_name: d?.name ?? null,
      // autofill a partir do device se os campos do chip estiverem vazios
      icc: f.icc || d?.icc || '',
      operadora: f.operadora || d?.operator || '',
      numero: f.numero || d?.phone || '',
      status: f.status === 'estoque' ? 'ativo' : f.status,
    }));
  };

  const save = useMutation({
    mutationFn: () => {
      const payload: ChipInput = {
        ...form,
        icc: form.icc.trim(),
        custo_mensal:
          form.custo_mensal === null || form.custo_mensal === undefined || (form.custo_mensal as unknown) === ''
            ? null
            : Number(form.custo_mensal),
      };
      return isEdit ? chipsApi.update(chip!.id, payload) : chipsApi.create(payload);
    },
    onSuccess: onSaved,
    onError: (e) => setErr((e as Error).message),
  });

  return (
    <Modal
      title={isEdit ? `Editar chip ${chip!.icc}` : 'Novo chip'}
      onClose={onClose}
      footer={
        <>
          <Button variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button
            onClick={() => {
              setErr(null);
              if (!form.icc.trim()) {
                setErr('ICCID é obrigatório.');
                return;
              }
              save.mutate();
            }}
            disabled={save.isPending}
          >
            {save.isPending ? 'Salvando…' : 'Salvar'}
          </Button>
        </>
      }
    >
      <div className="space-y-3">
        <L label="ICCID *">
          <Input value={form.icc} onChange={(e) => set('icc', e.target.value)} placeholder="8988303000010526..." />
        </L>
        <div className="grid gap-3 sm:grid-cols-2">
          <L label="Linha (número do SIM)">
            <Input value={form.numero ?? ''} onChange={(e) => set('numero', e.target.value)} placeholder="5511..." />
          </L>
          <L label="Operadora">
            <Input value={form.operadora ?? ''} onChange={(e) => set('operadora', e.target.value)} placeholder="VIVO, CHIP MAX…" />
          </L>
          <L label="Fornecedor">
            <Input value={form.fornecedor ?? ''} onChange={(e) => set('fornecedor', e.target.value)} placeholder="Quem forneceu o chip" />
          </L>
          <L label="Plano de dados">
            <Input value={form.plano_dados ?? ''} onChange={(e) => set('plano_dados', e.target.value)} placeholder="Ex: M2M 50MB" />
          </L>
          <L label="Custo mensal (R$)">
            <Input
              type="number"
              step="0.01"
              value={form.custo_mensal ?? ''}
              onChange={(e) => set('custo_mensal', e.target.value === '' ? null : Number(e.target.value))}
              placeholder="0,00"
            />
          </L>
          <L label="Status">
            <select
              value={form.status}
              onChange={(e) => set('status', e.target.value as ChipStatus)}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              {STATUSES.map((s) => (
                <option key={s} value={s}>
                  {STATUS_LABEL[s]}
                </option>
              ))}
            </select>
          </L>
          <L label="Rastreador / Embarcação">
            <select
              value={form.device_uniqueid ?? ''}
              onChange={(e) => onPickDevice(e.target.value)}
              className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm"
            >
              <option value="">— sem vínculo —</option>
              {devices.map((d) => (
                <option key={d.id} value={d.uniqueId}>
                  {d.name} ({d.uniqueId})
                </option>
              ))}
            </select>
            <span className="text-[10px] text-muted-foreground">
              Vincula o rastreador (IMEI) e a embarcação ao chip.
            </span>
          </L>
        </div>
        <L label="Observações">
          <Input value={form.obs ?? ''} onChange={(e) => set('obs', e.target.value)} placeholder="Notas internas" />
        </L>
        {err && <p className="text-xs font-medium text-status-blocked">{err}</p>}
      </div>
    </Modal>
  );
}

function L({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block space-y-1">
      <span className="text-xs font-semibold text-muted-foreground">{label}</span>
      {children}
    </label>
  );
}
